/**
 * Color utilities — CSS variable resolution, hex-to-rgba, brightening.
 *
 * Canonical implementations consolidated from:
 * - packages/ssm-web5/src/utils/colors.js
 * - packages/hoi-poi-ui/src/utils/getVar.js
 * - apps/ssm-web4/src/app/utils/color/index.js
 */

/**
 * Resolve a CSS custom property value (e.g. `var(--color-primary)`).
 * Returns the input unchanged if it is not a `var(…)` expression.
 *
 * @param {string} cssVar
 * @returns {string}
 */
export const getVar = (cssVar) => {
    if (!cssVar?.startsWith('var(')) return cssVar;
    cssVar = cssVar.replace(/^var\(|\)$/g, '');
    const rootStyles = getComputedStyle(document.documentElement);
    return rootStyles.getPropertyValue(cssVar).trim();
};

/**
 * Convert a hex color (or CSS variable) to an `rgba(…)` string with the given
 * alpha. Supports shorthand hex (e.g. `#fff`).
 *
 * @param {string} hex — hex color or `var(--…)` string
 * @param {number} a — alpha value (0–1)
 * @returns {string} e.g. `rgba(255, 255, 255, 0.5)`
 */
export const alphaColor = (hex, a) => {
    if (hex.startsWith('var(')) hex = getVar(hex);

    // Expand shorthand (e.g., #fff to #ffffff)
    if (/^#([a-f\d]{3})$/i.test(hex)) {
        hex = hex.replace(
            /^#([a-f\d])([a-f\d])([a-f\d])$/i,
            (_, r, g, b) => `#${r}${r}${g}${g}${b}${b}`,
        );
    }

    const rgb = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return rgb
        ? `rgba(${parseInt(rgb[1], 16)}, ${parseInt(rgb[2], 16)}, ${parseInt(rgb[3], 16)}, ${a})`
        : '';
};

/**
 * Parse a hex color string to `{ r, g, b }`.
 * @param {string} hex
 * @returns {{ r: number, g: number, b: number }}
 */
const hexToRgb = (hex) => {
    hex = hex.replace(/^#/, '');
    return {
        r: parseInt(hex.substring(0, 2), 16),
        g: parseInt(hex.substring(2, 4), 16),
        b: parseInt(hex.substring(4, 6), 16),
    };
};

/**
 * Parse an `rgb(…)` or `rgba(…)` string to `{ r, g, b, a }`.
 *
 * Uses split/parseInt/parseFloat instead of a regex to avoid ReDoS — any
 * regex with quantifiers inside an optional group can exhibit super-linear
 * backtracking on crafted inputs.
 *
 * @param {string} color
 * @returns {{ r: number, g: number, b: number, a: number }}
 */
function parseRgb(color) {
    const s = color.trim();
    const isRgba = s.startsWith('rgba(');
    if ((!isRgba && !s.startsWith('rgb(')) || !s.endsWith(')')) {
        throw new Error('Invalid RGB or RGBA color string');
    }
    const inner = s.slice(isRgba ? 5 : 4, -1);
    const parts = inner.split(',');
    if (parts.length < 3 || parts.length > 4) {
        throw new Error('Invalid RGB or RGBA color string');
    }
    const r = parseInt(parts[0], 10);
    const g = parseInt(parts[1], 10);
    const b = parseInt(parts[2], 10);
    const a = parts.length === 4 ? parseFloat(parts[3]) : 1;
    if (isNaN(r) || isNaN(g) || isNaN(b) || isNaN(a)) {
        throw new Error('Invalid RGB or RGBA color string');
    }
    return { r, g, b, a };
}

/**
 * Brighten (lighten) a color by a percentage and optionally override alpha.
 *
 * @param {Object} params
 * @param {string} params.color — hex, rgb(…), rgba(…), or var(…)
 * @param {number} [params.bright=0] — percentage to brighten (0–100)
 * @param {number} [params.alpha] — override alpha
 * @returns {string} `rgba(…)` string
 */
export const brightenColor = ({ color, bright: percent = 0, alpha }) => {
    let r, g, b, a;

    if (color.startsWith('var(')) color = getVar(color);

    if (color.startsWith('#')) {
        ({ r, g, b } = hexToRgb(color));
        a = 1;
    } else if (color.startsWith('rgb')) {
        ({ r, g, b, a } = parseRgb(color));
    } else {
        throw new Error('Unsupported color format');
    }

    r = Math.min(255, Math.floor(r + (255 - r) * (percent / 100)));
    g = Math.min(255, Math.floor(g + (255 - g) * (percent / 100)));
    b = Math.min(255, Math.floor(b + (255 - b) * (percent / 100)));

    return `rgba(${r}, ${g}, ${b}, ${alpha || a})`;
};

/**
 * Determine whether an RGB array should be considered dark for contrast.
 *
 * @param {number[]} rgbColor
 * @param {Object} [options]
 * @param {number[]} [options.grayscaleWeights=[0.299, 0.587, 0.114]]
 * @param {number} [options.threshold=170]
 * @returns {boolean}
 */
export const isColorDark = (
    rgbColor,
    { grayscaleWeights = [0.299, 0.587, 0.114], threshold = 170 } = {},
) => {
    const [r, g, b] = grayscaleWeights;
    const grayscaleValue = r * rgbColor[0] + g * rgbColor[1] + b * rgbColor[2];

    return grayscaleValue < threshold;
};

/**
 * Generate a random hexadecimal color, optionally avoiding a list of
 * previously used colors.
 *
 * @param {string[]} [previousColors]
 * @returns {string} e.g. `#A3F29B`
 */
export const getRandomHexadecimalColor = (previousColors) => {
    const letters = '0123456789ABCDEF';
    let color = '#';
    for (let i = 0; i < 6; i++) {
        color += letters[Math.floor(Math.random() * 16)];
    }

    if (previousColors?.length && previousColors.includes(color)) {
        return getRandomHexadecimalColor(previousColors);
    }
    return color;
};
